import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AppShell } from "../components/AppShell";
import { PageHeader } from "../components/PageHeader";
import { Toolbar, ToolbarGroup, ToolbarSpacer } from "../components/Toolbar";
import { currentUtcMonth, shiftMonth } from "../lib/readingsApi";
import {
  fetchSyncPreview,
  fetchSyncStatus,
  runFirestoreSync,
  SYNC_PACKS_PER_INVOKE,
  type SyncBatchResult,
  type SyncPreviewResponse,
  type SyncStatusResponse,
} from "../lib/syncApi";

export function SyncPage() {
  const [month, setMonth] = useState(currentUtcMonth);
  const [preview, setPreview] = useState<SyncPreviewResponse | null>(null);
  const [status, setStatus] = useState<SyncStatusResponse | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SyncBatchResult | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [previewData, statusData] = await Promise.all([
        fetchSyncPreview(month),
        fetchSyncStatus().catch(() => null),
      ]);
      setPreview(previewData);
      if (statusData) setStatus(statusData);
      setSelected(new Set(previewData.packs.map((p) => p.recordName)));
    } catch (err) {
      setPreview(null);
      setError(err instanceof Error ? err.message : "Failed to load sync preview.");
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    void load();
  }, [load]);

  const packs = preview?.packs ?? [];
  const allSelected = packs.length > 0 && packs.every((p) => selected.has(p.recordName));
  const selectedCount = useMemo(
    () => packs.filter((p) => selected.has(p.recordName)).length,
    [packs, selected],
  );

  function toggle(name: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }

  function toggleAll() {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(packs.map((p) => p.recordName)));
  }

  async function onSync() {
    const names = packs.filter((p) => selected.has(p.recordName)).map((p) => p.recordName);
    if (names.length === 0) {
      setError("Select at least one published pack.");
      return;
    }
    setBusy(true);
    setError(null);
    setResult(null);
    setProgress(null);
    try {
      const data = await runFirestoreSync(month, names, (p) => {
        setProgress(`Chunk ${p.chunkIndex}/${p.chunkTotal}`);
      });
      setResult(data);
      const statusData = await fetchSyncStatus().catch(() => null);
      if (statusData) setStatus(statusData);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sync failed.");
    } finally {
      setBusy(false);
      setProgress(null);
    }
  }

  const firestoreOk = preview?.firestoreConfigured ?? status?.firestoreConfigured ?? false;

  return (
    <AppShell>
      <PageHeader
        eyebrow="Workflow"
        title="Firestore sync"
        description={
          <>
            One-way CloudKit → Firestore mirror for Android. Syncs{" "}
            <strong>published</strong> packs to <code>readingPacks/{"{dateKey}_{lang}"}</code> and
            days to <code>liturgicalDays/{"{dateKey}"}</code>. Batches of{" "}
            <strong>{SYNC_PACKS_PER_INVOKE}</strong> packs per request.
          </>
        }
        actions={
          <Toolbar label="Month">
            <ToolbarGroup>
              <button
                type="button"
                className="btn btn-quiet btn-sm"
                onClick={() => setMonth((m) => shiftMonth(m, -1))}
                disabled={busy}
                aria-label="Previous month"
              >
                ←
              </button>
              <button type="button" className="btn btn-quiet btn-sm" disabled>
                {month}
              </button>
              <button
                type="button"
                className="btn btn-quiet btn-sm"
                onClick={() => setMonth((m) => shiftMonth(m, 1))}
                disabled={busy}
                aria-label="Next month"
              >
                →
              </button>
            </ToolbarGroup>
            <button
              type="button"
              className="btn btn-quiet btn-sm"
              onClick={() => setMonth(currentUtcMonth())}
              disabled={busy}
            >
              This month
            </button>
            <button
              type="button"
              className="btn btn-quiet btn-sm"
              disabled={busy || loading}
              onClick={() => void load()}
            >
              Refresh
            </button>
          </Toolbar>
        }
      />

      <div className="meta-strip day-meta">
        <span className="meta-pill">
          Firestore{" "}
          <strong>{firestoreOk ? "configured" : "not configured"}</strong>
        </span>
        {(preview?.projectId || status?.projectId) && (
          <span className="meta-pill">
            Project <strong>{preview?.projectId ?? status?.projectId}</strong>
          </span>
        )}
        <span className="meta-pill">
          Published <strong>{packs.length}</strong>
        </span>
        {preview && (
          <span className="meta-pill">
            Env <strong>{preview.environment}</strong>
          </span>
        )}
      </div>

      {!firestoreOk && (
        <div className="error-banner" role="status" style={{ marginBottom: 12 }}>
          Set Pages secrets <code>FIREBASE_PROJECT_ID</code>,{" "}
          <code>FIRESTORE_CLIENT_EMAIL</code>, and <code>FIRESTORE_PRIVATE_KEY</code> (service
          account with Cloud Datastore User). See README.
        </div>
      )}

      <div className="panel panel-pad stack" style={{ marginBottom: 16 }}>
        <Toolbar label="Sync actions">
          <ToolbarSpacer />
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy || !firestoreOk || selectedCount === 0}
            onClick={() => void onSync()}
          >
            {busy ? "Syncing…" : `Sync selected (${selectedCount})`}
          </button>
          <Link className="btn btn-quiet" to="/publish">
            Review & publish
          </Link>
        </Toolbar>
        {progress && (
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>
            {progress}
          </p>
        )}
      </div>

      {error && (
        <div className="error-banner" role="alert" style={{ marginBottom: 12 }}>
          {error}
        </div>
      )}

      {preview?.warnings?.map((warning) => (
        <div key={warning} className="error-banner" role="status" style={{ marginBottom: 12 }}>
          {warning}
        </div>
      ))}

      {result && (
        <div
          className={result.failed > 0 ? "error-banner" : "banner-ok"}
          role="status"
          style={{ marginBottom: 12 }}
        >
          Synced {result.succeeded}/{result.attempted} packs · {result.daysSynced} day(s)
          {result.errorSummary ? ` · ${result.errorSummary}` : ""}
        </div>
      )}

      {loading && !preview ? (
        <div className="panel panel-pad" role="status">
          Loading published packs…
        </div>
      ) : packs.length === 0 ? (
        <div className="panel empty-panel">
          <h2>No published packs</h2>
          <p>
            Nothing published for {month}. Publish drafts first, then sync to Firestore.
          </p>
          <Toolbar>
            <Link className="btn btn-primary" to="/publish">
              Review & publish
            </Link>
            <Link className="btn btn-quiet" to="/readings">
              Calendar
            </Link>
          </Toolbar>
        </div>
      ) : (
        <div className="panel panel-pad stack" style={{ marginBottom: 16 }}>
          <label className="publish-select-all">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={toggleAll}
              disabled={busy}
            />
            <span>Select all ({packs.length})</span>
          </label>

          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th style={{ width: 36 }} />
                  <th>Date</th>
                  <th>Lang</th>
                  <th>Title</th>
                  <th>Firestore path</th>
                </tr>
              </thead>
              <tbody>
                {packs.map((pack) => (
                  <tr key={pack.recordName}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selected.has(pack.recordName)}
                        onChange={() => toggle(pack.recordName)}
                        disabled={busy}
                        aria-label={`Select ${pack.recordName}`}
                      />
                    </td>
                    <td>
                      <Link className="link-inline" to={`/readings/${pack.dateKey}`}>
                        {pack.dateKey}
                      </Link>
                    </td>
                    <td>
                      <code>{pack.languageCode}</code>
                    </td>
                    <td>{pack.liturgicalTitle ?? "—"}</td>
                    <td className="muted mono" style={{ fontSize: 12 }}>
                      readingPacks/{pack.dateKey}_{pack.languageCode}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {status?.recentRuns && status.recentRuns.length > 0 && (
        <div className="panel panel-pad stack">
          <div className="section-title">
            <h2>Recent sync runs</h2>
            <p>CloudKit <code>SyncRun</code> audit records (best-effort).</p>
          </div>
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Run</th>
                  <th>Status</th>
                  <th>Packs</th>
                  <th>Started</th>
                </tr>
              </thead>
              <tbody>
                {status.recentRuns.map((run) => (
                  <tr key={run.runId}>
                    <td>
                      <code>{run.runId}</code>
                    </td>
                    <td>
                      <span className={`chip ${run.status === "failed" ? "neutral" : ""}`}>
                        {run.status}
                      </span>
                    </td>
                    <td>{run.packsSynced ?? "—"}</td>
                    <td className="muted" style={{ fontSize: 13 }}>
                      {run.startedAt ? new Date(run.startedAt).toLocaleString() : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </AppShell>
  );
}
